import {Trans} from '@lingui/macro';
import {isFunction} from 'lodash';
import React, {FC} from 'react';

import {KeyboardActionsEnum} from '../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonChrome} from '../../../libs/shared/ui/src/components/buttons/buttonChrome';
import {
  ButtonVariantsEnum,
  findButtonColors,
} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {
  LightUserThemeProvider,
  styled,
  useUserTheme,
} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {LeftPanel} from '../app/leftPanel';
import {LeftPanelTopbar} from '../app/leftPanelTopbar/leftPanelTopbar';
import {TooltipLabelWithShortcut} from '../common/tooltips/tooltipLabelWithShortcut';
import {RenderLargeCalendarsLeftPanel} from './largeCalendarsBase';
import {LargeCalendarsContainer} from './largeCalendarsContainer';

/*
 * Props.
 */

interface TabLargeCalendarsProps {
  panelId: CalendarsPanelId;
  isVisible: boolean;
  canCreateNewEvent: boolean;
  onNewEventClick: Handler;
  workspacesWidth: number;
  onWorkspacesWidthChange: HandlerOf<number>;
}

const StyledButtonChrome = styled(ButtonChrome)`
  width: 100%;
  max-width: 137px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  flex: 1;
`;

const StyledCreateEventButton = styled(ChromelessButton)`
  flex-grow: 1;
`;

const StyledCreateEventButtonContent = styled(ButtonContent)`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  grid-column-gap: 6px;
  justify-content: center;

  padding: 6px 18px 7px 16px;
`;

const StyledCreateEventButtonIcon = styled(Icon)`
  margin-top: -1px;
  margin-bottom: -2px;
`;

const StyledCreateEventButtonLabel = styled.div`
  ${center()}
`;

/*
 * Component.
 */

const newEventButtonVariant = ButtonVariantsEnum.PRIMARY;

export const TabLargeCalendars: FC<TabLargeCalendarsProps> = (props) => {
  const {panelId, isVisible, canCreateNewEvent, onNewEventClick, workspacesWidth, onWorkspacesWidthChange} =
    props;

  const theme = useUserTheme();
  const buttonColors = findButtonColors(theme);
  const newEventButtonColors = buttonColors[newEventButtonVariant];

  const actualIconColor = isFunction(newEventButtonColors.text)
    ? newEventButtonColors.text()
    : newEventButtonColors.text;

  const renderCreateEventButton = () => (
    <LeftPanelTopbar>
      <StyledButtonChrome
        variant={newEventButtonVariant}
        size={VisualSizesEnum.MEDIUM}
        isDisabled={!canCreateNewEvent}
      >
        {(buttonProps) => (
          <StyledTooltipCoordinator
            render={() => (
              <TooltipLabelWithShortcut
                label={<Trans id="calendars-createevent">New event</Trans>}
                keyboardAction={KeyboardActionsEnum.COMPOSE}
              />
            )}
          >
            <StyledCreateEventButton
              {...buttonProps}
              onClick={onNewEventClick}
              interactionId="createevent-button"
            >
              <StyledCreateEventButtonContent {...buttonProps}>
                <StyledCreateEventButtonIcon
                  name="newEvent"
                  color={!canCreateNewEvent ? newEventButtonColors.textDisabled : actualIconColor}
                />
                <StyledCreateEventButtonLabel>
                  <Trans id="calendars-createevent">New event</Trans>
                </StyledCreateEventButtonLabel>
              </StyledCreateEventButtonContent>
            </StyledCreateEventButton>
          </StyledTooltipCoordinator>
        )}
      </StyledButtonChrome>
    </LeftPanelTopbar>
  );

  // Use the standard LeftPanel to render the date picker and calendars list.
  const renderLeftPanel: RenderLargeCalendarsLeftPanel = (width, content) => (
    <LeftPanel panelId={undefined} interactionId="calendars" topBar={renderCreateEventButton()}>
      {content}
    </LeftPanel>
  );

  return (
    // TODO: Remove theme provider when we fully support dark mode in calendar.
    <LightUserThemeProvider>
      <LargeCalendarsContainer
        panelId={panelId}
        isVisible={isVisible}
        renderLeftPanel={renderLeftPanel}
        leftPanelWidth={workspacesWidth}
        onLeftPanelWidthChange={onWorkspacesWidthChange}
      />
    </LightUserThemeProvider>
  );
};
